import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { FiTrash } from 'react-icons/fi';
import { MdOutlineCopyAll } from 'react-icons/md';
import deleteCompanyOptionApi from '@/api/delete/company/delete-company-option';
import getCompanyOptionApi from '@/api/get/company/company-options/get-company-option';
import Alert from '@/components/alert/alert-component';
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '@/components/button/button-component';
import Loader from '@/components/loader/loader-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import { Col } from '@/components/grid/grid-component';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import { useMutation, useQuery } from '@/hooks/use-api';
import Styled from './option-view-style';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import processOptionData from './options-utils';
import OptionsVariantComponent from './option-variant/options-variant';
import OptionViewPage from '@/components/option/option-view-page';
import BreadcrumbPortal from '@/components/breadcrumb/breadcrumb-portal';
import OptionViewParentModal from '@/components/option/option-view-parent-modal';

/**
 *
 *
 * @returns {JSX.Element}
 */
const OptionView = () => {
  const { id, optionId } = useParams();

  const navigate = useNavigate();

  const [showParentModal, setShowParentModal] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const { data, loading } = useQuery(getCompanyOptionApi, id, optionId);

  const {
    mutateAsync: deleteCompanyOption,
    isPending: deleteCompanyOptionLoading,
  } = useMutation({
    mutationKey: ['delete'],
    mutationFn: async () => deleteCompanyOptionApi(id, optionId),
    onSuccess: () => {
      navigate(`/app/company-management/view/${id}/options`);
      Alert({
        icon: 'success',
        text: 'Option deleted successfully',
      });
    },
  });

  const launchDeleteConfirmation = useCallback(() => {
    ConfirmationModal({
      title: 'Delete Option',
      text: `Are you sure you want to delete this option?`,
      onConfirm: () => deleteCompanyOption(),
    });
  }, [deleteCompanyOption]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setInitialLoading(true);
        const response = await getCompanyOptionApi(id, optionId);

        const { prices = [] } = response.data;
        const pricesWithoutParent = prices.filter(
          (price) => price.parent_option === null,
        );

        // Show modal if we have multiple prices without parents
        if (pricesWithoutParent.length > 1) {
          setShowParentModal(true);
        }
      } finally {
        setInitialLoading(false);
      }
    };

    if (id && optionId) {
      fetchInitialData();
    }
  }, [id, optionId]);

  const isLoading = initialLoading || loading;
  const shouldShowForm = !initialLoading && !showParentModal;

  if (isLoading || deleteCompanyOptionLoading) {
    return (
      <Styled.Div>
        <Loader loading />
      </Styled.Div>
    );
  }

  return (
    <>
      <OptionViewParentModal
        isOpen={showParentModal}
        onClose={() => {
          navigate(`/app/company-management/view/${id}/options`);
        }}
        onConfirm={() =>
          navigate(
            `/app/company-management/view/${id}/options/edit/${optionId}`,
          )
        }
        loading={isLoading}
      />
      <Styled.MainOption>
        <Col>
          <PermissionWrapper
            permissions={['option.create', 'option.update', 'option.delete']}
          >
            <BreadcrumbPortal>
              <PrimaryOutlineButton
                buttonWidth="108px"
                type="button"
                loading={false}
                title="Duplicate"
                onClick={() =>
                  navigate(`/app/company-management/view/${id}/options/new`, {
                    state: { isOptionDuplicate: optionId },
                  })
                }
                icon={<MdOutlineCopyAll />}
                margin="0 0 0 0"
              />
              <PrimaryOutlineButton
                buttonWidth="108px"
                type="button"
                loading={false}
                title="Delete"
                onClick={() => launchDeleteConfirmation()}
                icon={<FiTrash />}
                margin="0 16px 0 16px"
              />
              <PrimaryButton
                buttonWidth="108px"
                type="submit"
                loading={loading || deleteCompanyOptionLoading}
                title="Edit"
                onClick={() =>
                  navigate(
                    `/app/company-management/view/${id}/options/edit/${optionId}`,
                  )
                }
              />
            </BreadcrumbPortal>
          </PermissionWrapper>
          {shouldShowForm && (
            <OptionViewPage data={data} parts={data?.option?.parts} />
          )}
        </Col>
      </Styled.MainOption>
      {shouldShowForm && (
        <Styled.MainVariant>
          <FormWrapper
            defaultValues={async () =>
              processOptionData(id, optionId, {
                isEdit: true,
                selectedParentId: null,
              })
            }
            onSubmit={() => {}}
          >
            <OptionsVariantComponent
              type="view"
              name="variant"
              createVariantDefaults={() => {}}
              originalOptions={null}
            />
          </FormWrapper>
        </Styled.MainVariant>
      )}
    </>
  );
};

OptionView.propTypes = {};

OptionView.defaultProps = {};

export default OptionView;
